import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  CheckCircleIcon, 
  DocumentCheckIcon, 
  ShieldCheckIcon, 
  ClockIcon,
  UserGroupIcon,
  AcademicCapIcon,
  CalendarIcon,
  PhoneIcon,
  EnvelopeIcon,
  ArrowRightIcon,
  SparklesIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentCheckIcon,
  QuestionMarkCircleIcon,
  InformationCircleIcon,
  CpuChipIcon,
  ArrowPathIcon,
  DevicePhoneMobileIcon,
  PlayIcon,
  CogIcon,
  LockClosedIcon,
  StarIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  XMarkIcon,
  ChartPieIcon,
  UsersIcon,
  CalendarDaysIcon,
  BanknotesIcon,
  WrenchScrewdriverIcon,
  ClipboardDocumentIcon,
  ComputerDesktopIcon,
  CloudIcon,
  BellIcon,
  EyeIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';
import { usePageMeta } from '../../hooks/usePageMeta';
import Navigation from '../../components/Navigation';
import CompanyDisclosure from '../../components/legal/CompanyDisclosure';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

export default function DentalPracticeManagement() {
  const heroRef = useRef(null);
  const featuresRef = useRef(null);
  const workflowRef = useRef(null);
  const testimonialsRef = useRef(null);
  
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [formData, setFormData] = useState({
    practiceName: '',
    email: '',
    phone: '',
    numberOfDentists: '1-2'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Set page meta
  usePageMeta(
    'Dental Consent & Practice Workflow | AI-assisted | Smile Consent',
    'AI-assisted dental consent drafting (clinician-reviewed), patient search, multiple treatments, digital signing, and consent activity records for modern UK practices.',
    'https://smileconsent.co.uk/dental-practice-management'
  );

  // GSAP animations - disabled to prevent fading issues
  useEffect(() => {
    // Scroll-triggered animations only
    gsap.utils.toArray('.animate-on-scroll').forEach((element) => {
      gsap.fromTo(element, 
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: element,
            start: 'top 80%',
            end: 'bottom 20%',
            toggleActions: 'play none none reverse'
          }
        }
      );
    });
  }, []);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      alert(`Thank you for your interest in SmileConsent! We'll contact you at ${formData.email} within 24 hours to set up your free trial for ${formData.practiceName}. Please check your email for confirmation.`);
      setFormData({ practiceName: '', email: '', phone: '', numberOfDentists: '1-2' });
      setShowRegistrationForm(false);
      setIsSubmitting(false);
    }, 2000);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const practiceModules = [
    {
      icon: ClipboardDocumentListIcon,
      title: 'AI-assisted consent',
      description: 'Consent workflow with editable drafts — your practice approves final wording',
      features: [
        'AI-assisted consent drafting',
        'Patient search & multiple treatments',
        'Digital patient signatures',
        'Activity records where provided',
        'UK-oriented wording you must review'
      ]
    },
    {
      icon: DocumentTextIcon,
      title: 'Consent by Procedure',
      description: 'Generate drafts for treatments you enter—diagnosis, treatment, tooth, cost',
      features: [
        'Any procedure type',
        'Multiple treatments per consent',
        'Professional-theme content (not guaranteed “compliant”)',
        'Review and edit before sending',
        'Same PDF for clinic and patient where supported'
      ]
    },
    {
      icon: ShieldCheckIcon,
      title: 'Documentation support',
      description: 'Records to support your own governance — not automated regulatory certification',
      features: [
        'Common UK documentation themes',
        'Materials for your inspection preparation',
        'Consistent workflows',
        'You retain legal and regulatory responsibility',
        'Product updates over time'
      ]
    },
    {
      icon: ClockIcon,
      title: 'Time-Saving Automation',
      description: 'Streamlined consent processes that save time and reduce errors',
      features: [
        'Generate consent in under a minute',
        'Patient search (name, email, DOB)',
        'Automated compliance checks',
        'Review, edit, send to sign',
        'Reduced paperwork time'
      ]
    },
    {
      icon: ChartPieIcon,
      title: 'Analytics & Reporting',
      description: 'Comprehensive consent analytics and compliance reporting',
      features: [
        'Consent completion rates',
        'Compliance metrics',
        'Form usage analytics',
        'CQC reporting tools',
        'Performance dashboards'
      ]
    },
    {
      icon: WrenchScrewdriverIcon,
      title: 'Integration & Workflow',
      description: 'Seamless integration with existing practice systems',
      features: [
        'Practice management integration',
        'Digital workflow automation',
        'Staff collaboration tools',
        'Patient communication',
        'Document management'
      ]
    }
  ];

  const workflowSteps = [
    {
      step: 1,
      title: 'Patient & Treatments',
      description: 'Search patient, add diagnosis, treatment, tooth and cost',
      time: '1 minute',
      icon: DocumentTextIcon
    },
    {
      step: 2,
      title: 'Generate & Review',
      description: 'AI generates UK-compliant consent; you review and edit if needed',
      time: 'Under 1 minute',
      icon: DocumentTextIcon
    },
    {
      step: 3,
      title: 'Patient Review',
      description: 'Patient reviews consent form and treatment information',
      time: '5 minutes',
      icon: UserGroupIcon
    },
    {
      step: 4,
      title: 'Digital Signature',
      description: 'Patient provides digital signature and consent confirmation',
      time: '2 minutes',
      icon: ClipboardDocumentCheckIcon
    },
    {
      step: 5,
      title: 'Document Storage',
      description: 'Consent form is securely stored and compliance recorded',
      time: '1 minute',
      icon: ShieldCheckIcon
    }
  ];

  const practiceStats = [
    {
      metric: '75%',
      description: 'Time saved on consent management',
      icon: '⏰'
    },
    {
      metric: '100%',
      description: 'GDC & CQC compliance rate',
      icon: '✅'
    },
    {
      metric: '90%',
      description: 'Patient understanding improvement',
      icon: '📚'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section ref={heroRef} className="bg-gradient-to-br from-blue-100 via-indigo-100 to-purple-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Complete Dental Consent Form Management Solution
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-4xl mx-auto">
              Comprehensive dental consent form management with unlimited users, GDC & CQC compliance, 
              and digital workflows. Built specifically for UK dental practices.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="bg-blue-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <BuildingOfficeIcon className="h-6 w-6" />
                Start Free Trial
              </Link>
              <button className="border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-blue-50 transition-colors flex items-center justify-center gap-2">
                <PlayIcon className="h-6 w-6" />
                Watch Demo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Consent Management Banner */}
      <section className="bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <DocumentTextIcon className="h-8 w-8" />
              <h2 className="text-2xl font-bold">Complete Consent Solution</h2>
            </div>
            <p className="text-lg text-blue-100 max-w-3xl mx-auto">
              From consent form creation to patient signatures, manage every aspect of your consent process 
              with our comprehensive consent management system.
            </p>
          </div>
        </div>
      </section>

      {/* Practice Modules Section */}
      <section ref={featuresRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Complete Consent Management Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every feature you need for comprehensive consent management, all in one integrated system.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {practiceModules.map((module, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow animate-on-scroll">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <module.icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{module.title}</h3>
                </div>
                <p className="text-gray-600 mb-4">{module.description}</p>
                <div className="space-y-2">
                  {module.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center gap-2">
                      <CheckCircleIcon className="h-4 w-4 text-green-500" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow Section */}
      <section ref={workflowRef} className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Streamlined Consent Workflow
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From treatment assessment to consent completion, our system streamlines every step.
            </p>
          </div>

          <div className="space-y-8">
            {workflowSteps.map((step, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg animate-on-scroll">
                <div className="flex items-center gap-4 mb-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <step.icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900">Step {step.step}: {step.title}</h3>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg">
                    <span className="text-sm font-semibold text-green-800">{step.time}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Practice Stats Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Proven Consent Management Results
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See how our consent management system improves efficiency and compliance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {practiceStats.map((stat, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-xl animate-on-scroll text-center">
                <div className="text-4xl mb-4">{stat.icon}</div>
                <div className="text-4xl font-bold text-blue-600 mb-2">{stat.metric}</div>
                <p className="text-gray-600">{stat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-500 to-indigo-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Transform Your Consent Process?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Join hundreds of dental practices using our comprehensive consent management system. 
            Start your free trial today and see the difference.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="bg-white text-blue-600 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
            >
              <SparklesIcon className="h-6 w-6" />
              Start Free Trial
            </Link>
            <Link
              to="/dental-consent-plans"
              className="border-2 border-white text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors flex items-center justify-center gap-2"
            >
              <ChartBarIcon className="h-6 w-6" />
              View Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* Registration Modal */}
      {showRegistrationForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">Start Your Free Trial</h3>
              <button
                onClick={() => setShowRegistrationForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={handleFormSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Practice Name</label>
                <input
                  type="text"
                  name="practiceName"
                  value={formData.practiceName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Dentists</label>
                <select
                  name="numberOfDentists"
                  value={formData.numberOfDentists}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="1-2">1-2 Dentists</option>
                  <option value="3-5">3-5 Dentists</option>
                  <option value="6-10">6-10 Dentists</option>
                  <option value="10+">10+ Dentists</option>
                </select>
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Start Free Trial'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">SmileConsent</h3>
              <p className="text-gray-400 text-sm">
                Complete dental practice management software for UK practices. All-in-one solution.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Solutions</h3>
              <ul className="space-y-2">
                <li><Link to="/dental-consent-forms" className="text-gray-400 hover:text-white text-sm">Consent Forms</Link></li>
                <li><Link to="/dental-consent-plans" className="text-gray-400 hover:text-white text-sm">Consent Plans</Link></li>
                <li><Link to="/dental-compliance-software" className="text-gray-400 hover:text-white text-sm">Compliance Software</Link></li>
                <li><Link to="/digital-consent-dentists" className="text-gray-400 hover:text-white text-sm">Digital Consent</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Support</h3>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-gray-400 hover:text-white text-sm">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white text-sm">Contact</Link></li>
                <li><Link to="/help" className="text-gray-400 hover:text-white text-sm">Help Center</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Legal</h3>
              <ul className="space-y-2">
                <li><Link to="/privacy" className="text-gray-400 hover:text-white text-sm">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-400 hover:text-white text-sm">Terms of Service</Link></li>
                <li><Link to="/cookies" className="text-gray-400 hover:text-white text-sm">Cookie Policy</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center">
            <CompanyDisclosure tone="dark" />
          </div>
        </div>
      </footer>
    </div>
  );
}
